import { Alert, Card, Center, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { QueryKey } from "@tanstack/react-query";
import { Table as ReactTable } from "@tanstack/react-table";
import { forwardRef, useImperativeHandle } from "react";
import NoData from "@/components/no-data";
import Table from "@/components/table";
import ResetTableButton from "@/components/table/ResetTableButton";
import { THandler } from "@/components/table/Table";
import TablePagination from "@/components/table/TablePagination";
import useQueryTable from "@/components/table/useQueryTable";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import colaboradoresColumns from "./Columns";
import { DEFAULT_PAGINATION, DEFAULT_SORTING, getColaboradores } from "../../firestore";

export const QUERY_KEY: string = "colaboradores-list";

const ColaboradoresTable = forwardRef<THandler<TColaborador>>((_properties, reference) => {
  const uid = getCurrentUser()?.uid;
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

  const { table, data, isLoading, error, filters, refetch } = useQueryTable<TColaborador>({
    queryKey: [QUERY_KEY],
    enabled: !!uid && !!activeEmpresaId,
    subjectType: "TColaborador",
    columns: colaboradoresColumns,
    initialState: {
      columnFilters: [],
      globalFilter: "",
      pagination: DEFAULT_PAGINATION,
      sorting: DEFAULT_SORTING
    },
    queryFunction: ({ sorting, filters, pagination, paginationCursors }) => {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }
      return getColaboradores(activeEmpresaId, {
        sorting,
        filters,
        pagination,
        paginationCursors
      });
    }
  });

  useImperativeHandle<THandler<TColaborador>, THandler<TColaborador>>(reference, () => {
    return {
      table,
      data,
      isLoading,
      error,
      filters
    };
  }, [filters, data, error, isLoading, table]);

  /**
   * Handles the close of the error alert.
   */
  function handleErrorClose() {
    // queryClient.removeQueries({ queryKey: QUERY_KEY })
    // remove();
    void refetch();
  }

  const isEmpty = !isLoading && data?.entries.length === 0;

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size="1rem" />}
        title="Erro ao buscar colaboradores"
        color="red"
        radius={0}
        withCloseButton
        onClose={handleErrorClose}
      >
        {error.message}
      </Alert>
    );
  }
  return (
    <>
      <Card.Section withBorder>
        <Table table={table} isLoading={isLoading} />

        {isEmpty && !error && !isLoading && (
          <Center m="lg">
            <NoData m="md">
              <Stack gap="xs">
                <Text c="dimmed">Nenhum Colaborador Encontrado!</Text>
                <ResetTableButton table={table as ReactTable<unknown>} queryKey={QUERY_KEY as unknown as QueryKey}>
                  Limpar filtros
                </ResetTableButton>
              </Stack>
            </NoData>
          </Center>
        )}
      </Card.Section>

      {reference && "current" in reference && reference.current !== null ? (
        <Card.Section withBorder>
          <TablePagination tableHandlers={reference.current} />
        </Card.Section>
      ) : null}
    </>
  );
});

ColaboradoresTable.displayName = "ColaboradoresTable";

export default ColaboradoresTable;
